import * as React from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Progress } from "@/components/ui/progress"
import { api } from "@/lib/api"
import {
  Activity,
  ShoppingCart,
  CreditCard,
  Clock,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowRight,
} from "lucide-react"

function QueueMonitor() {
  const { data: queues, isLoading, refetch } = useQuery({
    queryKey: ["queues"],
    queryFn: () => api.get("/queues"),
    refetchInterval: 5000,
  })

  const queueData = queues?.data || []

  const getQueueIcon = (name) => {
    const icons = {
      purchases: <ShoppingCart className="h-5 w-5" />,
      support: <CreditCard className="h-5 w-5" />,
      scans: <Activity className="h-5 w-5" />,
      proxies: <ArrowRight className="h-5 w-5" />,
      notifications: <Clock className="h-5 w-5" />,
    }
    return icons[name] || <Activity className="h-5 w-5" />
  }

  const getQueueColor = (pending, failed) => {
    if (failed > 0) return "destructive"
    if (pending > 100) return "warning"
    return "success"
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Queue Monitor</h1>
          <p className="text-muted-foreground">Real-time queue status and management</p>
        </div>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Queue Cards */}
      <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {queueData.map((queue) => {
          const total = (queue.pending_count || 0) + (queue.processing_count || 0) + 
                       (queue.completed_count || 0) + (queue.failed_count || 0)
          const progress = total > 0 ? ((queue.completed_count || 0) / total) * 100 : 0

          return (
            <Card key={queue.id} className="relative overflow-hidden">
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium flex items-center gap-2">
                  {getQueueIcon(queue.name)}
                  {queue.name}
                </CardTitle>
                <Badge variant={getQueueColor(queue.pending_count, queue.failed_count)}>
                  {queue.is_active ? "Active" : "Paused"}
                </Badge>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Progress Bar */}
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span>{Math.round(progress)}%</span>
                    </div>
                    <Progress value={progress} className="h-2" />
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-warning">{queue.pending_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Waiting</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-info">{queue.processing_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Processing</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-success">{queue.completed_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Completed</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-destructive">{queue.failed_count || 0}</div>
                      <div className="text-xs text-muted-foreground">Failed</div>
                    </div>
                  </div>

                  {/* Visual Queue */}
                  <div className="h-8 rounded-lg bg-muted overflow-hidden flex">
                    {queue.pending_count > 0 && (
                      <div
                        className="bg-warning h-full flex items-center justify-center text-xs font-medium"
                        style={{ width: `${(queue.pending_count / Math.max(total, 1)) * 100}%` }}
                      >
                        {queue.pending_count}
                      </div>
                    )}
                    {queue.processing_count > 0 && (
                      <div
                        className="bg-info h-full flex items-center justify-center text-xs font-medium"
                        style={{ width: `${(queue.processing_count / Math.max(total, 1)) * 100}%` }}
                      >
                        {queue.processing_count}
                      </div>
                    )}
                    {queue.completed_count > 0 && (
                      <div
                        className="bg-success h-full flex items-center justify-center text-xs font-medium"
                        style={{ width: `${(queue.completed_count / Math.max(total, 1)) * 100}%` }}
                      >
                        {queue.completed_count}
                      </div>
                    )}
                    {queue.failed_count > 0 && (
                      <div
                        className="bg-destructive h-full flex items-center justify-center text-xs font-medium"
                        style={{ width: `${(queue.failed_count / Math.max(total, 1)) * 100}%` }}
                      >
                        {queue.failed_count}
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" className="flex-1">
                      View Items
                    </Button>
                    <Button size="sm" variant="outline">
                      Clear Completed
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          )
        })}
      </div>

      {/* Queue Summary */}
      <Card>
        <CardHeader>
          <CardTitle>Queue Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 md:grid-cols-4">
            <div className="text-center p-4 border rounded-lg">
              <div className="text-3xl font-bold">
                {queueData.reduce((acc, q) => acc + (q.pending_count || 0), 0)}
              </div>
              <div className="text-sm text-muted-foreground">Total Pending</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-3xl font-bold">
                {queueData.reduce((acc, q) => acc + (q.processing_count || 0), 0)}
              </div>
              <div className="text-sm text-muted-foreground">Total Processing</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-3xl font-bold text-success">
                {queueData.reduce((acc, q) => acc + (q.completed_count || 0), 0)}
              </div>
              <div className="text-sm text-muted-foreground">Total Completed</div>
            </div>
            <div className="text-center p-4 border rounded-lg">
              <div className="text-3xl font-bold text-destructive">
                {queueData.reduce((acc, q) => acc + (q.failed_count || 0), 0)}
              </div>
              <div className="text-sm text-muted-foreground">Total Failed</div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

export default QueueMonitor
